import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import dayjs from 'dayjs';
import { Loader2 } from 'lucide-react';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import DatePopoverSingle from '@/components/DatePopoverSingle';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover.tsx';
import LoadingOpacityDialog from '@/UI/LoadingOpacityDialog';

import { GetCompanyItemsDataApi } from '../../api/CompanyItems.api';
import type {
  ICompanyItemGroupedInterface,
  ICompanyItemInterface,
  ICompanyList,
} from '../../api/CompanyItems.interface';

import { AutoCompleteCompanies } from './CompaniesList';
import OpenSearchBtn from './OpenSearchBtn';

// Zod schema for form validation
const formSchema = z
  .object({
    company: z
      .object({
        ID: z.number(),
        Name: z.string(),
      })
      .nullable(),
    startDate: z.string().min(1, 'بداية المدة مطلوبة'),
    endDate: z.string().min(1, 'نهاية المدة مطلوبة'),
  })
  .refine((data) => data.company !== null, {
    message: 'الشركة مطلوبة',
    path: ['company'],
  })
  .refine(
    (data) => {
      return new Date(data.endDate) >= new Date(data.startDate);
    },
    {
      message: 'تاريخ النهاية يجب أن يكون بعد تاريخ البداية',
      path: ['endDate'],
    },
  );

type FormData = z.infer<typeof formSchema>;

type Props = {
  setAllData: (
    value: React.SetStateAction<ICompanyItemInterface[] | null>,
  ) => void;
  setFilterData: (
    value: React.SetStateAction<ICompanyItemInterface[] | null>,
  ) => void;
  setGroupedData: React.Dispatch<
    React.SetStateAction<ICompanyItemGroupedInterface[] | null>
  >;
  setSelectedCompany: React.Dispatch<React.SetStateAction<ICompanyList | null>>;
  setStartDate: React.Dispatch<React.SetStateAction<Date | null>>;
  setEndDate: React.Dispatch<React.SetStateAction<Date | null>>;
  setLoading: (value: React.SetStateAction<boolean>) => void;
  companies: ICompanyList[];
  loading: boolean;
};

export function FormDialog({
  setAllData,
  setLoading,
  companies,
  loading,
  setFilterData,
  setGroupedData,
  setEndDate,
  setSelectedCompany,
  setStartDate,
}: Props) {
  const [isOpen, SetIsOpen] = React.useState(false);

  const getItemsMutation = useMutation({
    mutationFn: GetCompanyItemsDataApi,
    onSuccess: async (data) => {
      setAllData(data?.data?.transactions);
      setFilterData(data?.data?.transactions);
      setGroupedData(data?.data?.groupItems);
      setLoading(false);
      toast.success('تم جلب الاصناف بنجاح', {
        position: 'bottom-center',
      });
      if (data?.data?.count === 0 || !data?.data?.transactions) {
        toast.error('لا توجد حركات لاصناف هذه الشركة خلال هذه المدة', {
          position: 'top-right',
        });
      }
    },

    onError: (err: AxiosError<{ message?: string }>) => {
      setLoading(false);
      toast.error(err?.response?.data?.message || 'حدث خطأ ما', {
        position: 'top-right',
      });
    },
  });

  // React Hook Form setup
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      startDate: '',
      endDate: '',
      company: companies[20] || null,
    },
  });

  const onSubmit = async (values: FormData) => {
    setAllData(null);
    setGroupedData(null);
    setFilterData(null);
    setLoading(true);
    SetIsOpen(false);
    if (values?.company?.ID) {
      await getItemsMutation.mutateAsync({
        id: values?.company?.ID,
        startDate: dayjs(values.startDate).format('YYYY-MM-DD'),
        endDate: dayjs(values.endDate).format('YYYY-MM-DD'),
      });
    }
  };

  const {
    formState: { errors, isSubmitting },
    handleSubmit,
    setValue,
    watch,
    reset,
  } = form;
  const formValues = watch();

  return (
    <Popover open={isOpen} modal onOpenChange={SetIsOpen}>
      <PopoverTrigger asChild>
        <OpenSearchBtn values={formValues} loading={loading} />
      </PopoverTrigger>
      <PopoverContent className="relative w-80">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-4">
            {/* Company Selection */}
            <div>
              <Badge color="secondary" className="mb-2 rounded-md">
                الشركة
              </Badge>
              <AutoCompleteCompanies
                options={companies}
                searchPlaceholder="ابحث عن الشركة"
                onValueChange={(data) => {
                  setValue('company', data);
                  setSelectedCompany(data || null);
                }}
                value={formValues.company}
              />
              {errors.company && (
                <p className="mt-1 text-sm text-red-500">
                  {errors.company.message}
                </p>
              )}
            </div>

            {/* Start Date Field */}
            <div>
              <Badge color="secondary" className="mb-2 rounded-md">
                بداية المدة
              </Badge>
              <DatePopoverSingle
                value={formValues?.startDate || null}
                selectFn={(dateString) => {
                  setValue('startDate', dateString);
                  setStartDate(new Date(dateString));
                }}
                placeholder="اختر تاريخ البداية"
                width="w-full"
              />

              {errors.startDate && (
                <p className="mt-1 text-sm text-red-500">
                  {errors.startDate.message}
                </p>
              )}
            </div>

            {/* End Date Field */}
            <div>
              <Badge color="secondary" className="mb-2 rounded-md">
                نهاية المدة
              </Badge>
              <DatePopoverSingle
                value={formValues?.endDate || null}
                selectFn={(dateString) => {
                  setValue('endDate', dateString);
                  setEndDate(new Date(dateString));
                }}
                placeholder="اختر تاريخ النهاية"
                width="w-full"
              />

              {errors.endDate && (
                <p className="mt-1 text-sm text-red-500">
                  {errors.endDate.message}
                </p>
              )}
            </div>

            {/* Submit Button */}
            <div className="flex gap-2">
              <Button
                type="submit"
                disabled={isSubmitting}
                size="lg"
                variant="destructive"
                className="h-10 w-full"
              >
                {isSubmitting ? (
                  <Loader2
                    className="animate-spin text-muted"
                    size={20}
                    aria-label="Loading Spinner"
                    data-testid="loader"
                  />
                ) : (
                  'بحث'
                )}
              </Button>

              {/* Reset Button */}
              <Button
                type="button"
                disabled={isSubmitting}
                size="lg"
                variant="outline"
                className="h-10 w-full"
                onClick={() => {
                  setAllData(null);
                  SetIsOpen(false);
                  reset();
                }}
              >
                إعادة تعيين
              </Button>
            </div>
          </div>
        </form>
        {loading && <LoadingOpacityDialog />}
      </PopoverContent>
    </Popover>
  );
}
